import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './asset-info.style.js';
import { updateAsset } from '@cmms/api';
import { db } from '../../../../services/auth.service.js';

@customElement('asset-info')
export class AssetInfo extends LitElement {
  @property({ type: Object }) asset: any = null;
  @property({ type: String }) companyId: string = '';
  @property({ type: Array }) locations: Array<{ id: string; altId?: string; name: string }> = [];

  // form + change tracking
  @state() private form: any = {};
  @state() private original: any = {};
  @state() private isSaving = false;
  @state() private error: string | null = null;
  @state() private isEditing = false;

  static styles = styles;

  updated(changed: Map<string, unknown>) {
    if (changed.has('asset') && this.asset) {
      this.initializeForm();
    }
    if (changed.has('locations')) {
      if (this.locations.length > 0) {
        // Wait for the next tick to ensure the select component is fully rendered
        setTimeout(() => {
          this.normalizeSelectedLocation();
          // Force a complete re-render by updating the form
          if (this.form?.location_id) {
            const currentLocationId = this.form.location_id;
            this.form = { ...this.form, location_id: '' };
            setTimeout(() => {
              this.form = { ...this.form, location_id: currentLocationId };
            }, 10);
          }
        }, 0);
      }
    }
    
    // Also normalize when form is initialized and locations are already available
    if (changed.has('form') && this.locations.length > 0 && this.form?.location_id) {
      setTimeout(() => {
        this.normalizeSelectedLocation();
      }, 0);
    }
    if (changed.has('form')) {
      this.notifyParentOfChanges();
    }
  }

  private notifyParentOfChanges() {
    this.dispatchEvent(new CustomEvent('form-changed', {
      detail: { hasChanges: this.isDirty() },
      bubbles: true,
      composed: true
    }));
  }

  private initializeForm() {
    if (!this.asset) return;

    const getValue = (field: string) => {
      if ((this.asset as any)[field] !== undefined) {
        return (this.asset as any)[field];
      }
      if ((this.asset as any).model && (this.asset as any).model[field] !== undefined) {
        return (this.asset as any).model[field];
      }
      return undefined;
    };

    this.form = {
      name: getValue('name') ?? '',
      status: getValue('status') ?? 'Active',
      location_id: getValue('location_id') ?? '',
      cost: getValue('cost') ?? null,
      purchase_date: getValue('purchase_date') ?? null,
      warranty_date: getValue('warranty_date') ?? null,
      photo_url: getValue('photo_url') ?? null
    };
    
    this.original = JSON.parse(JSON.stringify(this.form));
    this.normalizeSelectedLocation();
  }

  private normalizeSelectedLocation() {
    const current = this.form?.location_id;
    
    if (current == null || current === '') return;
    const currentStr = String(current);
    const match = (this.locations || []).find(l =>
      String(l.id) === currentStr || String(l.altId ?? '') === currentStr
    );
    
    if (match) {
      // Always ensure we use the correct ID format from the locations list
      const correctId = String(match.id);
      if (String(current) !== correctId) {
        this.form = { ...this.form, location_id: correctId };
      } else {
        // Force a re-render to ensure the select shows the correct value
        // Use setTimeout to ensure the select component is fully rendered
        setTimeout(() => {
          this.requestUpdate();
          // Also try to manually set the select value
          const select = this.shadowRoot?.querySelector('#location-select') as any;
          if (select) {
            select.value = correctId;
          }
        }, 100);
      }
    }
  }

  private isDirty() {
    for (const key of Object.keys(this.form)) {
      if ((this.form as any)[key] !== (this.original as any)[key]) {
        return true;
      }
    }
    return false;
  }

  private formatDateForInput(ts: number | null) {
    if (!ts) return '';
    const d = new Date(ts);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  }

  private parseDateInput(val: string): number | null {
    if (!val) return null;
    const t = new Date(val).getTime();
    return Number.isNaN(t) ? null : t;
  }

  private downloadQRCode() {
    if (this.asset?.qr_code_url) {
      const link = document.createElement('a');
      link.href = this.asset.qr_code_url;
      link.download = `asset-${this.asset.id || this.asset.name}-qr-code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  private parseNumberInput(val: string): number | null {
    if (!val) return null;
    const n = Number(val);
    return Number.isNaN(n) ? null : n;
  }

  private handleEdit() {
    this.isEditing = true;
  }

  private handleCancel() {
    this.isEditing = false;
    this.form = JSON.parse(JSON.stringify(this.original));
    this.error = null;
  }

  private async handleSave() {
    if (!this.isDirty()) {
      this.isEditing = false;
      return;
    }

    try {
      this.isSaving = true;
      this.error = null;
      
      const update = this.buildUpdate();
      
      if (Object.keys(update).length) {
        const assetId = this.asset?.id ?? this.asset?.asset_id;
        
        // Use the proper API
        const result = await updateAsset(db, assetId, update);
        
        if (result.error) {
          throw new Error(result.error);
        }
      }
      
      this.original = JSON.parse(JSON.stringify(this.form));
      this.isEditing = false;
      
      this.dispatchEvent(new CustomEvent('asset-updated', {
        detail: { asset: { ...this.asset, ...update } }, 
        bubbles: true, 
        composed: true
      }));
    } catch (e: any) {
      this.error = e?.message ?? 'Failed to save changes';
    } finally {
      this.isSaving = false;
    }
  }

  private buildUpdate(): Record<string, any> {
    const keys = ['name','status','location_id','cost','purchase_date','warranty_date','photo_url'];
    const out: any = {};
    for (const k of keys) if ((this.form?.[k] ?? null) !== (this.original?.[k] ?? null)) out[k] = this.form[k];
    if (Object.keys(out).length) out.updated_at = Date.now();
    return out;
  }

  render() {
    if (!this.asset) return html``;

    const assetName = this.form.name || this.asset.name || '';
    const assetParent = this.asset.parent_name || 'Root Asset';

    return html`
      <div class="container">
        <div class="info-description">
          Information - Track any piece of information for your asset including IDs, meter readings, manuals and more.
        </div>

        ${this.error ? html`<sl-alert variant="danger" open>${this.error}</sl-alert>` : null}

        <div class="field-row">
          <div class="field-label">Name:</div>
          <div class="field-value">
            <sl-input size="large" .value=${this.form.name ?? ''}
                      @sl-input=${(e: any) => this.form = { ...this.form, name: e.target.value }}
                      @sl-hide=${(e: Event) => e.stopPropagation()}
                      ?disabled=${!this.isEditing}></sl-input>
          </div>
        </div>

        <div class="field-row">
          <div class="field-label">Status:</div>
          <div class="field-value">
            <sl-select placeholder="Select a status" .value=${this.form.status ?? ''}
                       @sl-change=${(e: any) => this.form = { ...this.form, status: e.target.value }}
                       @sl-hide=${(e: Event) => e.stopPropagation()}
                       @sl-show=${(e: Event) => e.stopPropagation()}
                       @sl-after-show=${(e: Event) => e.stopPropagation()}
                       @sl-after-hide=${(e: Event) => e.stopPropagation()}
                       ?disabled=${!this.isEditing}>
              
              <sl-option value="Active">Active</sl-option>
              <sl-option value="Inactive">Inactive</sl-option>
            </sl-select>
          </div>
        </div>

        <div class="field-row">
          <div class="field-label">Parent Asset:</div>
          <div class="field-value">
            <sl-input .value=${assetParent} disabled></sl-input>
          </div>
        </div>

        <div class="field-row">
          <div class="field-label">Location:</div>
          <div class="field-value">
            <sl-select id="location-select" placeholder="Select location"
                       .value=${String(this.form.location_id ?? '')}
                       key=${`location-select-${this.form.location_id}-${this.locations.length}`}
                       @sl-change=${(e: any) => this.form = { ...this.form, location_id: String(e.target.value) }}
                       @sl-hide=${(e: Event) => e.stopPropagation()}
                       @sl-show=${(e: Event) => e.stopPropagation()}
                       @sl-after-show=${(e: Event) => e.stopPropagation()}
                       @sl-after-hide=${(e: Event) => e.stopPropagation()}
                       ?disabled=${!this.isEditing}>
              <sl-option value="">No location</sl-option>
              
              ${(this.locations || []).map(l => html`<sl-option value=${String(l.id)}>${l.name}</sl-option> `) }
            </sl-select>
          </div>
        </div>

        <div class="field-row">
          <div class="field-label">Cost:</div>
          <div class="field-value">
            <sl-input type="number" .value=${this.form.cost ?? ''} placeholder="Enter cost"
                      @sl-input=${(e: any) => {
                        const v = e.target.value;
                        this.form = { ...this.form, cost: v === '' ? null : Number(v) };
                      }}
                      @sl-hide=${(e: Event) => e.stopPropagation()}
                      ?disabled=${!this.isEditing}>
              <sl-icon name="currency-dollar" slot="prefix"></sl-icon>
            </sl-input>
          </div>
        </div>

        <div class="field-row">
          <div class="field-label">Purchase Date:</div>
          <div class="field-value">
            <sl-input type="date"
                      .value=${this.formatDateForInput(this.form.purchase_date)}
                      placeholder="Select purchase date"
                      @sl-input=${(e: any) => this.form = { ...this.form, purchase_date: this.parseDateInput(e.target.value) }}
                      @sl-hide=${(e: Event) => e.stopPropagation()}
                      ?disabled=${!this.isEditing}>
              <sl-icon name="calendar" slot="prefix"></sl-icon>
            </sl-input>
          </div>
        </div>

        <div class="field-row">
          <div class="field-label">Warranty Date:</div>
          <div class="field-value">
            <sl-input type="date"
                      .value=${this.formatDateForInput(this.form.warranty_date)}
                      placeholder="Select warranty date"
                      @sl-input=${(e: any) => this.form = { ...this.form, warranty_date: this.parseDateInput(e.target.value) }}
                      @sl-hide=${(e: Event) => e.stopPropagation()}
                      ?disabled=${!this.isEditing}>
              <sl-icon name="shield" slot="prefix"></sl-icon>
            </sl-input>
          </div>
        </div>


        ${this.asset?.fields && this.asset.fields.length > 0 ? html`
          ${this.asset.fields.map((field: { name: string; value: string }) => html`
            <div class="field-row">
              <div class="field-label">${field.name}:</div>
              <div class="field-value">
                <sl-input .value=${field.value || ''}
                          @sl-input=${(e: any) => { field.value = e.target.value; }}
                          @sl-hide=${(e: Event) => e.stopPropagation()}
                          placeholder="Enter ${field.name.toLowerCase()}" size="large"
                          ?disabled=${!this.isEditing}>
                </sl-input>
              </div>
            </div>
          `)}
        ` : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asset-info': AssetInfo;
  }
}
